import { Typography } from '@euler/app/components/typography';
import { aiColors } from '@euler/app/flows/ai-suite/theme';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { roleNameFromRoleType } from '@euler/functions/user/util';
import {
  CoachingCaseCard,
  CoachingCaseDetail,
  CoachingNamedItem,
} from '@euler/model/coaching';
import { ChevronRight } from 'lucide-react-native';
import { memo, useMemo } from 'react';
import { Pressable, ScrollView, StyleSheet, View } from 'react-native';
import { CaseTags } from '../../components/CaseTags';
import { Chip } from '../../components/Chip';
import { channelName, formatName, levelName, turnsLabel } from '../../copy';

/**
 * A case as the trainee may see it before practice: what they know going
 * in, what it practises, and how it is played. Never a hidden fact.
 */
export const CaseDetailView = memo(
  ({
    detail,
    onOpenRelated,
    onOpenAllRelated,
    onOpenTag,
  }: {
    detail: CoachingCaseDetail;
    onOpenRelated: (card: CoachingCaseCard) => void;
    /** 查看全部: shown when more are related than the page lists. */
    onOpenAllRelated: () => void;
    /** Opens the library with this tag selected. */
    onOpenTag: (tag: CoachingNamedItem) => void;
  }) => {
    const styles = useStyles();
    const { rpx } = useResponsiveContext();
    const facts: [string, string][] = [
      ['形式', formatName(detail.format)],
      ['预计时长', turnsLabel(detail.expectedTurns)],
      ['可选难度', detail.levels.map(level => levelName(level)).join('、')],
      ['适合岗位', detail.traineeRoles.map(roleNameFromRoleType).join('、')],
    ];
    return (
      <ScrollView contentContainerStyle={styles.page}>
        <View style={styles.chips}>
          <Chip
            text={`${detail.category.name} · ${detail.parentCategory.name}`}
          />
          {detail.additionalCategories.map(item => (
            <Chip
              key={item.slug}
              text={item.name}
              color={aiColors.mutedForeground}
            />
          ))}
        </View>
        <Typography.Text
          size={rpx(20)}
          bold
          color={aiColors.foreground}
          style={styles.title}
        >
          {detail.title}
        </Typography.Text>
        <Typography.Text
          size={rpx(14)}
          color={aiColors.mutedForeground}
          lineHeight={rpx(22)}
          style={styles.caption}
        >
          {detail.caption}
        </Typography.Text>

        <View style={styles.card}>
          <Typography.Text size={rpx(14)} bold color={aiColors.foreground}>
            你知道的情况
          </Typography.Text>
          {detail.initiallyKnown.map((item, i) => (
            <View key={i} style={styles.bullet}>
              <View style={styles.dot} />
              <Typography.Text
                size={rpx(14)}
                color={aiColors.foreground}
                lineHeight={rpx(22)}
                style={styles.flex}
              >
                {item}
              </Typography.Text>
            </View>
          ))}
        </View>

        <View style={styles.card}>
          <Typography.Text size={rpx(14)} bold color={aiColors.foreground}>
            练习重点
          </Typography.Text>
          <View style={styles.chipRow}>
            {detail.criteria.map(item => (
              <Chip key={item.slug} text={item.name} color={aiColors.green} />
            ))}
          </View>
        </View>

        <View style={[styles.card, styles.grid]}>
          {facts.map(([label, value]) => (
            <View key={label} style={styles.cell}>
              <Typography.Text size={rpx(11)} color={aiColors.mutedForeground}>
                {label}
              </Typography.Text>
              <Typography.Text
                size={rpx(14)}
                medium
                color={aiColors.foreground}
              >
                {value}
              </Typography.Text>
            </View>
          ))}
          <View style={styles.cellWide}>
            <Typography.Text size={rpx(11)} color={aiColors.mutedForeground}>
              可能发生的场景
            </Typography.Text>
            <Typography.Text size={rpx(14)} medium color={aiColors.foreground}>
              {detail.channels.map(item => channelName(item)).join(' · ')}
            </Typography.Text>
          </View>
        </View>

        <CaseTags tags={detail.tags} onPress={onOpenTag} />

        {detail.related.length ? (
          <View style={styles.section}>
            <Typography.Text size={rpx(16)} bold color={aiColors.foreground}>
              相关案例
            </Typography.Text>
            {detail.related.map(card => (
              <Pressable
                key={card.id}
                onPress={() => onOpenRelated(card)}
                accessibilityRole="button"
                style={styles.related}
              >
                <Typography.Text
                  size={rpx(11)}
                  color={aiColors.mutedForeground}
                >
                  {`${card.category.name} · ${card.parentCategory.name}`}
                </Typography.Text>
                <Typography.Text
                  size={rpx(14)}
                  bold
                  color={aiColors.foreground}
                >
                  {card.title}
                </Typography.Text>
                <Typography.Text
                  size={rpx(12)}
                  color={aiColors.mutedForeground}
                  numberOfLines={2}
                  lineHeight={rpx(18)}
                >
                  {card.caption}
                </Typography.Text>
              </Pressable>
            ))}
            {detail.relatedTotal > detail.related.length ? (
              <Pressable
                onPress={onOpenAllRelated}
                accessibilityRole="button"
                style={styles.allRelated}
              >
                <Typography.Text size={rpx(13)} medium color={aiColors.primary}>
                  {`查看全部 ${detail.relatedTotal} 个相关案例`}
                </Typography.Text>
                <ChevronRight size={rpx(16)} color={aiColors.primary} />
              </Pressable>
            ) : null}
          </View>
        ) : null}
      </ScrollView>
    );
  },
);

function useStyles() {
  const { rpx } = useResponsiveContext();
  return useMemo(
    () =>
      StyleSheet.create({
        page: {
          width: '100%',
          maxWidth: rpx(768),
          alignSelf: 'center',
          padding: rpx(16),
          paddingBottom: rpx(40),
        },
        chips: { flexDirection: 'row', flexWrap: 'wrap', gap: rpx(6) },
        chipRow: {
          flexDirection: 'row',
          flexWrap: 'wrap',
          gap: rpx(6),
          marginTop: rpx(12),
        },
        title: { marginTop: rpx(12) },
        caption: { marginTop: rpx(8) },
        card: {
          marginTop: rpx(16),
          borderRadius: rpx(14),
          borderWidth: 1,
          borderColor: aiColors.border,
          backgroundColor: aiColors.card,
          padding: rpx(16),
        },
        bullet: { flexDirection: 'row', gap: rpx(10), marginTop: rpx(10) },
        dot: {
          width: rpx(6),
          height: rpx(6),
          borderRadius: rpx(3),
          marginTop: rpx(8),
          backgroundColor: aiColors.primary,
        },
        flex: { flex: 1 },
        grid: { flexDirection: 'row', flexWrap: 'wrap', rowGap: rpx(14) },
        cell: { width: '50%', gap: rpx(2) },
        cellWide: { width: '100%', gap: rpx(2) },
        section: { marginTop: rpx(28), gap: rpx(10) },
        allRelated: {
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          gap: rpx(2),
          paddingVertical: rpx(10),
        },
        related: {
          borderRadius: rpx(14),
          borderWidth: 1,
          borderColor: aiColors.border,
          backgroundColor: aiColors.card,
          padding: rpx(14),
          gap: rpx(4),
        },
      }),
    [rpx],
  );
}
